import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountBalance as CreditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/api';

const API_URL = `${API_BASE_URL}/api`;

const ApplySchemeCreditDialog = ({ open, onClose, customerTokenNo, billId, billAmount, onSuccess, previewMode = false }) => {
  const [schemes, setSchemes] = useState([]);
  const [selectedSchemeId, setSelectedSchemeId] = useState('');
  const [amountToApply, setAmountToApply] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingSchemes, setFetchingSchemes] = useState(false);
  const [error, setError] = useState(null);
  const [existingUsage, setExistingUsage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (open && customerTokenNo) {
      fetchAvailableSchemes();
      if (billId && !previewMode) {
        fetchExistingUsage();
      }
    }
  }, [open, customerTokenNo, billId, previewMode]);

  const fetchExistingUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/bills/${billId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const bill = response.data.bill;
      if (bill.SchemeCreditApplied && parseFloat(bill.SchemeCreditApplied) > 0) {
        setIsEditMode(true);
        setAmountToApply(bill.SchemeCreditApplied.toString());
        // Note: We'll need to fetch which scheme was used
        // For now, we'll allow changing the amount
      }
    } catch (err) {
      console.error('Error fetching existing usage:', err);
    }
  };

  const fetchAvailableSchemes = async () => {
    try {
      setFetchingSchemes(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/schemes/customer/${customerTokenNo}/available-credit`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSchemes(response.data.schemes || []);
      
      // Auto-select first scheme if only one available
      if (response.data.schemes && response.data.schemes.length === 1) {
        setSelectedSchemeId(response.data.schemes[0].SchemeId.toString());
      }
    } catch (err) {
      console.error('Error fetching schemes:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch available schemes');
    } finally {
      setFetchingSchemes(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!isEditMode && !selectedSchemeId) {
      setError('Please select a scheme');
      return;
    }

    if (!amountToApply || parseFloat(amountToApply) < 0) {
      setError('Please enter a valid amount (0 or greater)');
      return;
    }
    
    // Allow 0 in edit mode to remove credit
    if (!isEditMode && parseFloat(amountToApply) <= 0) {
      setError('Please enter an amount greater than 0');
      return;
    }

    const selectedScheme = schemes.find(s => s.SchemeId.toString() === selectedSchemeId);
    if (!selectedScheme) {
      setError('Selected scheme not found');
      return;
    }

    const amount = parseFloat(amountToApply);
    const availableCredit = parseFloat(selectedScheme.AvailableCredit);

    if (amount > availableCredit) {
      setError(`Amount exceeds available credit (₹${availableCredit.toFixed(2)})`);
      return;
    }

    if (billAmount && amount > parseFloat(billAmount)) {
      setError(`Amount exceeds bill amount (₹${parseFloat(billAmount).toFixed(2)})`);
      return;
    }

    // If in preview mode (no billId), just return the selected amount
    if (previewMode || !billId) {
      onSuccess && onSuccess({
        schemeId: parseInt(selectedSchemeId),
        appliedAmount: amount,
        remainingCredit: availableCredit - amount,
        notes: notes
      });
      
      // Reset form
      setSelectedSchemeId('');
      setAmountToApply('');
      setNotes('');
      
      onClose();
      return;
    }

    // Otherwise, apply or update credit to existing bill
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      
      let response;
      if (isEditMode) {
        // Update existing credit
        response = await axios.put(
          `${API_URL}/schemes/credit/bill/${billId}`,
          {
            newAmount: amount,
            notes: notes
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        // Don't show toast here - will show when bill is saved
      } else {
        // Apply new credit
        response = await axios.post(
          `${API_URL}/schemes/credit/apply`,
          {
            schemeId: parseInt(selectedSchemeId),
            billId: billId,
            amountToApply: amount,
            notes: notes
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        // Don't show toast here - will show when bill is saved
      }

      // Reset form
      setSelectedSchemeId('');
      setAmountToApply('');
      setNotes('');
      setIsEditMode(false);

      onSuccess && onSuccess(response.data);
      onClose();
    } catch (err) {
      console.error('Error applying/updating credit:', err);
      const errorMessage = err.response?.data?.error?.message || 'Failed to apply/update credit';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedScheme = schemes.find(s => s.SchemeId.toString() === selectedSchemeId);
  const totalAvailableCredit = schemes.reduce((sum, s) => sum + parseFloat(s.AvailableCredit), 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          border: "1px solid #444",
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
        borderBottom: '2px solid #C4A574',
        color: '#fff'
      }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#C4A574' }}>
            {isEditMode ? 'Edit Scheme Credit' : 'Apply Scheme Credit'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Customer Token #{customerTokenNo}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#C4A574' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, bgcolor: 'transparent' }}>
        {fetchingSchemes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#C4A574' }} />
          </Box>
        ) : schemes.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              bgcolor: 'rgba(33, 150, 243, 0.1)', 
              color: '#90caf9',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              '& .MuiAlert-icon': { color: '#90caf9' }
            }}
          >
            No schemes with available credit found for this customer.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  bgcolor: 'rgba(139, 0, 0, 0.2)',
                  color: '#ff6b6b',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  '& .MuiAlert-icon': { color: '#ff6b6b' }
                }} 
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {/* Total Available Credit */}
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
              border: "1px solid #444"
            }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Available Credit
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#C4A574' }}>
                ₹{totalAvailableCredit.toFixed(2)}
              </Typography>
            </Paper>

            {/* Bill Amount Info */}
            {billAmount && (
              <Paper sx={{ 
                p: 2, 
                mb: 3, 
                background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
                border: "1px solid #444"
              }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Bill Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#C4A574' }}>
                  ₹{parseFloat(billAmount).toFixed(2)}
                </Typography>
              </Paper>
            )}

            {/* Edit Mode Info */}
            {isEditMode && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  bgcolor: 'rgba(33, 150, 243, 0.1)', 
                  color: '#90caf9',
                  border: '1px solid rgba(33, 150, 243, 0.3)',
                  '& .MuiAlert-icon': { color: '#90caf9' }
                }}
              >
                You are editing existing scheme credit. Enter 0 to remove the credit entirely.
              </Alert>
            )}

            {/* Scheme Selection */}
            {!isEditMode && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#fff' }}>
                Select Scheme
              </Typography>
            )}
            {!isEditMode && (
              <TableContainer 
                component={Paper} 
                sx={{ 
                  mb: 3,
                  background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
                  border: "1px solid #444",
                  overflowX: 'auto'
                }}
              >
                <Table size="small" sx={{ minWidth: 650 }}>
                  <TableHead sx={{ bgcolor: '#C4A574' }}>
                    <TableRow>
                      <TableCell sx={{ color: '#000', fontWeight: 600, py: 1.5, minWidth: 60 }}>Select</TableCell>
                      <TableCell sx={{ color: '#000', fontWeight: 600, py: 1.5, minWidth: 100 }}>Scheme ID</TableCell>
                      <TableCell sx={{ color: '#000', fontWeight: 600, py: 1.5, minWidth: 80 }}>Status</TableCell>
                      <TableCell sx={{ color: '#000', fontWeight: 600, py: 1.5, minWidth: 140 }}>Available Credit</TableCell>
                      <TableCell sx={{ color: '#000', fontWeight: 600, py: 1.5, minWidth: 120 }}>Start Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemes.map((scheme) => (
                      <TableRow 
                        key={scheme.SchemeId} 
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: selectedSchemeId === scheme.SchemeId.toString() ? 'rgba(196, 165, 116, 0.1)' : 'inherit',
                          '&:hover': {
                            bgcolor: 'rgba(196, 165, 116, 0.05)'
                          }
                        }}
                        onClick={() => setSelectedSchemeId(scheme.SchemeId.toString())}
                      >
                        <TableCell sx={{ py: 1.5, minWidth: 60 }}>
                          <Radio 
                            checked={selectedSchemeId === scheme.SchemeId.toString()}
                            onChange={() => setSelectedSchemeId(scheme.SchemeId.toString())}
                            sx={{ 
                              color: '#C4A574', 
                              '&.Mui-checked': { color: '#C4A574' },
                              p: 0.5
                            }} 
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#fff', py: 1.5, minWidth: 100, whiteSpace: 'nowrap' }}>
                          #{scheme.SchemeId}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 80 }}>
                          <Chip 
                            label={scheme.Status} 
                            sx={{ 
                              bgcolor: '#4caf50', 
                              color: '#fff',
                              fontSize: '0.75rem',
                              height: '24px'
                            }} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5, minWidth: 140 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#C4A574', whiteSpace: 'nowrap' }}>
                            ₹{parseFloat(scheme.AvailableCredit).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#fff', py: 1.5, minWidth: 120, whiteSpace: 'nowrap' }}>
                          {new Date(scheme.StartDate).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Amount Input */}
            {(selectedScheme || isEditMode) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedScheme && !isEditMode && (
                  <Paper sx={{ 
                    p: 2, 
                    background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
                    border: "1px solid #444"
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Selected Scheme Available Credit
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#C4A574' }}>
                      ₹{parseFloat(selectedScheme.AvailableCredit).toFixed(2)}
                    </Typography>
                  </Paper>
                )}

                <TextField
                  fullWidth
                  required
                  label={isEditMode ? "New Amount" : "Amount to Apply"}
                  type="number"
                  value={amountToApply}
                  onChange={(e) => setAmountToApply(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1, color: '#C4A574' }}>₹</Typography>
                  }}
                  helperText={
                    isEditMode 
                      ? "Enter 0 to remove scheme credit entirely" 
                      : selectedScheme 
                        ? `Maximum: ₹${parseFloat(selectedScheme.AvailableCredit).toFixed(2)}`
                        : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-focused": {
                        color: "#C4A574",
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "rgba(255, 255, 255, 0.6)",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Optional notes about this credit application"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-focused": {
                        color: "#C4A574",
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "rgba(255, 255, 255, 0.5)",
                      opacity: 1,
                    },
                  }}
                />

                {/* Calculation Summary */}
                {amountToApply && parseFloat(amountToApply) > 0 && (
                  <Paper sx={{ 
                    p: 2, 
                    background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
                    border: "1px solid #4caf50"
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4caf50' }}>
                      Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#fff' }}>Credit to Apply:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                          ₹{parseFloat(amountToApply).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#fff' }}>Remaining Credit:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                          ₹{(parseFloat(selectedScheme.AvailableCredit) - parseFloat(amountToApply)).toFixed(2)}
                        </Typography>
                      </Box>
                      {billAmount && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #444' }}>
                          <Typography variant="body2" sx={{ color: '#fff' }}>Bill Amount After Credit:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#C4A574', fontSize: '1.1rem' }}>
                            ₹{(parseFloat(billAmount) - parseFloat(amountToApply)).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
        borderTop: '1px solid #444' 
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <CreditIcon />}
          onClick={handleSubmit}
          disabled={loading || (!isEditMode && (!selectedSchemeId || schemes.length === 0)) || !amountToApply}
          sx={{ 
            bgcolor: '#C4A574', 
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#B39565' },
            '&:disabled': {
              bgcolor: 'rgba(196, 165, 116, 0.3)',
              color: 'rgba(0, 0, 0, 0.5)'
            }
          }}
        >
          {loading ? (isEditMode ? 'Updating...' : 'Applying...') : (isEditMode ? 'Update Credit' : 'Apply Credit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplySchemeCreditDialog;
